import { BOARD_SIZE, CENTRE_ROW, CENTRE_COL, } from './types.js';
// Official 15x15 Scrabble premium square layout. Indices are [row, col].
// TW = triple word, DW = double word, TL = triple letter, DL = double letter.
const PREMIUM_LAYOUT = {};
function set(r, c, p) {
    PREMIUM_LAYOUT[`${r},${c}`] = p;
}
// Typing the coordinate arrays as fixed tuples so destructured elements are
// `number` (not `number | undefined`) under `noUncheckedIndexedAccess`.
const TW_COORDS = [
    [0, 0],
    [0, 7],
    [0, 14],
    [7, 0],
    [7, 14],
    [14, 0],
    [14, 7],
    [14, 14],
];
const DW_COORDS = [
    // Main diagonal (excluding centre)
    [1, 1],
    [2, 2],
    [3, 3],
    [4, 4],
    [10, 10],
    [11, 11],
    [12, 12],
    [13, 13],
    // Anti-diagonal (excluding centre)
    [1, 13],
    [2, 12],
    [3, 11],
    [4, 10],
    [10, 4],
    [11, 3],
    [12, 2],
    [13, 1],
];
const TL_COORDS = [
    [1, 5],
    [1, 9],
    [5, 1],
    [5, 5],
    [5, 9],
    [5, 13],
    [9, 1],
    [9, 5],
    [9, 9],
    [9, 13],
    [13, 5],
    [13, 9],
];
const DL_COORDS = [
    [0, 3],
    [0, 11],
    [2, 6],
    [2, 8],
    [3, 0],
    [3, 7],
    [3, 14],
    [6, 2],
    [6, 6],
    [6, 8],
    [6, 12],
    [7, 3],
    [7, 11],
    [8, 2],
    [8, 6],
    [8, 8],
    [8, 12],
    [11, 0],
    [11, 7],
    [11, 14],
    [12, 6],
    [12, 8],
    [14, 3],
    [14, 11],
];
for (const [r, c] of TW_COORDS)
    set(r, c, 'TW');
for (const [r, c] of DW_COORDS)
    set(r, c, 'DW');
set(CENTRE_ROW, CENTRE_COL, 'STAR');
for (const [r, c] of TL_COORDS)
    set(r, c, 'TL');
for (const [r, c] of DL_COORDS)
    set(r, c, 'DL');
export function premiumAt(row, col) {
    return PREMIUM_LAYOUT[`${row},${col}`] ?? null;
}
export function emptyBoard() {
    const cells = [];
    for (let r = 0; r < BOARD_SIZE; r++) {
        for (let c = 0; c < BOARD_SIZE; c++) {
            cells.push({ row: r, col: c, premium: premiumAt(r, c), tile: null });
        }
    }
    return { cells };
}
export function cellAt(board, row, col) {
    if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
        throw new Error(`Cell out of bounds: ${row},${col}`);
    }
    return board.cells[row * BOARD_SIZE + col];
}
export function placeTile(board, row, col, letter, is_blank) {
    // Deep-clone each cell AND its tile so prior board states are immune to
    // future mutations of any tile object. The shallow `{ ...c }` alone leaves
    // every prior board pointing at the same tile, which becomes a footgun
    // when blank-designation logic mutates `letter` in place.
    const next = {
        cells: board.cells.map((c) => ({ ...c, tile: c.tile ? { ...c.tile } : null })),
    };
    const cell = next.cells[row * BOARD_SIZE + col];
    if (cell.tile)
        throw new Error(`Cell ${row},${col} already occupied`);
    cell.tile = { letter, is_blank };
    return next;
}
export function isEmpty(board) {
    return board.cells.every((c) => c.tile === null);
}
export function fromSerialized(serialized) {
    let board = emptyBoard();
    for (const p of serialized.placed) {
        board = placeTile(board, p.row, p.col, p.letter, p.is_blank);
    }
    return board;
}
export function toSerialized(board) {
    return {
        placed: board.cells
            .filter((c) => c.tile !== null)
            .map((c) => ({ row: c.row, col: c.col, letter: c.tile.letter, is_blank: c.tile.is_blank })),
    };
}
/**
 * Connectivity check: are all placed tiles in a single connected component
 * (orthogonal adjacency) reachable from the centre star? Empty board = trivially true.
 */
export function isConnectedThroughCentre(board) {
    const occupied = new Set(board.cells.filter((c) => c.tile !== null).map((c) => `${c.row},${c.col}`));
    if (occupied.size === 0)
        return true;
    if (!occupied.has(`${CENTRE_ROW},${CENTRE_COL}`))
        return false;
    const visited = new Set();
    const queue = [[CENTRE_ROW, CENTRE_COL]];
    const deltas = [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
    ];
    while (queue.length) {
        const head = queue.shift();
        const r = head[0];
        const c = head[1];
        const key = `${r},${c}`;
        if (visited.has(key))
            continue;
        visited.add(key);
        for (const [dr, dc] of deltas) {
            const nr = r + dr;
            const nc = c + dc;
            const nk = `${nr},${nc}`;
            if (occupied.has(nk) && !visited.has(nk))
                queue.push([nr, nc]);
        }
    }
    return visited.size === occupied.size;
}
//# sourceMappingURL=board.js.map